/**
 * "Paste image" button + the Cmd/Ctrl+V listener for one upload
 * surface. Drop it next to an existing Upload button and give it the
 * same handler:
 *
 *     <PasteImageButton
 *       onImage={(file) => uploadMutation.mutate(file)}
 *       busy={uploadMutation.isPending}
 *       testId="paste-featured-image-btn"
 *     />
 *
 * `hotkey` (default true) registers this surface on the shared paste
 * stack, so a keyboard paste anywhere on the page — outside text
 * fields — lands here. The stack is last-in-wins, so a modal that
 * mounts its own PasteImageButton takes the paste while it is open
 * and returns it on close (see `@/lib/clipboardImage`).
 */
import { Clipboard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGE_ACCEPT_ANY } from "@/lib/clipboardImage";
import { usePasteImage } from "@/hooks/usePasteImage";
import { tr } from "@/lib/i18n";

export default function PasteImageButton({
  onImage,
  accept = IMAGE_ACCEPT_ANY,
  busy = false,
  disabled = false,
  hotkey = true,
  label = tr("Paste"),
  title = tr("Paste an image from the clipboard (Cmd/Ctrl+V anywhere on this page)"),
  testId = "paste-image-btn",
  className =
    "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60",
  iconClass = "w-3.5 h-3.5",
  onError,
}) {
  const fail = onError || ((msg) => toast.error(msg));
  const { pasteFromClipboard, canReadClipboard } = usePasteImage({
    onImage,
    onError: fail,
    accept,
    // Deliberately NOT gated on `busy`: the paste stack is last-in-wins,
    // so unregistering mid-upload and re-registering afterwards would
    // move this surface back to the top — above a modal that opened in
    // the meantime, which would then lose its paste.
    enabled: hotkey && !disabled,
  });
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        if (!canReadClipboard) {
          fail(
            tr("This browser can't read the clipboard directly — press Cmd/Ctrl+V instead."),
          );
          return;
        }
        pasteFromClipboard();
      }}
      disabled={disabled || busy}
      title={title}
      className={className}
    >
      {busy ? (
        <Loader2 className={`${iconClass} animate-spin`} />
      ) : (
        <Clipboard className={iconClass} />
      )}
      {label}
    </button>
  );
}